import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import ToggleButton from "../../../components/common/Buttons/ToggleButton";
import InlineLoading from "../../../components/common/InlineLoading/InlineLoading";
import Tree, { ITree } from "../../../components/common/Tree/Tree";
import { RestrictedLayout } from "../../../components/layouts";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { RequestState } from "../../../enumerations/RequestState";
import { TrialLaboratoriesBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { TrialSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { ITmdState } from "../../../reducers";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialActions from "../../../actions/trial";
import * as TrialGroupLaboratoryActions from "../../../actions/trialGroupLaboratory";
import * as TrialInstitutionLaboratoryActions from "../../../actions/trialInstitutionLaboratory";
import * as Dtos from "../../../dtos/Tmd.dtos";
import * as AuthenticationHelper from "../../../helpers/authentication";

import "./TrialLaboratoriesPage.scss";
import moment from "moment";

interface TrialLaboratoriesPageProps {
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    trialGroupLaboratories: Dtos.TrialGroupLaboratory[];
    loadingTrialGroupLaboratories: boolean;
    loadTrialGroupLaboratoriesSuccess: boolean;
    loadTrialGroupLaboratoriesFailure: boolean;
    loadTrialGroupLaboratoriesErrorCode: string;
    trialInstitutionLaboratories: Dtos.TrialInstitutionLaboratory[];
    loadingTrialInstitutionLaboratories: boolean;
    loadTrialInstitutionLaboratoriesSuccess: boolean;
    loadTrialInstitutionLaboratoriesFailure: boolean;
    loadTrialInstitutionLaboratoriesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearTrialGroupLaboratories: TrialGroupLaboratoryActions.ITrialGroupLaboratoryClearActionCreator;
    loadTrialGroupLaboratories: TrialGroupLaboratoryActions.ITrialGroupLaboratoriesLoadByTrialIdActionCreator;
    clearTrialInstitutionLaboratories: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoryClearActionCreator;
    loadTrialInstitutionLaboratories: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoriesLoadByTrialIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialLaboratoriesPage: FC<TrialLaboratoriesPageProps> = ({
    trial,
    loadingTrial,
    loadTrialSuccess,
    loadTrialFailure,
    loadTrialErrorCode,
    trialGroupLaboratories,
    loadingTrialGroupLaboratories,
    loadTrialGroupLaboratoriesSuccess,
    loadTrialGroupLaboratoriesFailure,
    loadTrialGroupLaboratoriesErrorCode,
    trialInstitutionLaboratories,
    loadingTrialInstitutionLaboratories,
    loadTrialInstitutionLaboratoriesSuccess,
    loadTrialInstitutionLaboratoriesFailure,
    loadTrialInstitutionLaboratoriesErrorCode,
    permissions,
    clearTrial,
    loadTrial,
    clearTrialGroupLaboratories,
    loadTrialGroupLaboratories,
    clearTrialInstitutionLaboratories,
    loadTrialInstitutionLaboratories,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialLaboratories =
        !loadingTrial && 
        loadTrialSuccess && 
        !loadingTrialGroupLaboratories && 
        loadTrialGroupLaboratoriesSuccess &&
        !loadingTrialInstitutionLaboratories &&
        loadTrialInstitutionLaboratoriesSuccess &&
        !creatingTree;
    
    // Load Trial
    useEffect(() => {
        if (trialIdNumber && !trial && !loadingTrial) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialLaboratoriesBreadcrumbs(trialIdNumber));

            loadTrial(trialIdNumber);
        }
    }, [
        trialIdNumber,
        trial,
        loadingTrial,
        permissions
    ]);

    // Load Trial Group Laboratories
    useEffect(() => {
        if (trialIdNumber && !trialGroupLaboratories && !loadingTrialGroupLaboratories) {
            setTreeData(null);

            loadTrialGroupLaboratories(trialIdNumber);
        }
    }, [
        trialIdNumber,
        trialGroupLaboratories,
        loadingTrialGroupLaboratories
    ]);

    // Load Trial Institution Laboratories
    useEffect(() => {
        if (trialIdNumber && !trialInstitutionLaboratories && !loadingTrialInstitutionLaboratories) {
            setTreeData(null);

            loadTrialInstitutionLaboratories(trialIdNumber);
        }
    }, [
        trialIdNumber,
        trialInstitutionLaboratories,
        loadingTrialInstitutionLaboratories
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadTrialGroupLaboratoriesFailure) {
            navigate(`/error/${loadTrialGroupLaboratoriesErrorCode}`);
        }
        if (loadTrialInstitutionLaboratoriesFailure) {
            navigate(`/error/${loadTrialInstitutionLaboratoriesErrorCode}`);
        }
    }, [
        loadTrialFailure, 
        loadTrialErrorCode, 
        loadTrialGroupLaboratoriesFailure, 
        loadTrialGroupLaboratoriesErrorCode, 
        loadTrialInstitutionLaboratoriesFailure,
        loadTrialInstitutionLaboratoriesErrorCode
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialLaboratoriesBreadcrumbs(trialIdNumber, trial.displayName));
        }
    }, [
        trial,
        trialIdNumber,
        permissions
    ]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrial();
            clearTrialGroupLaboratories();
            clearTrialInstitutionLaboratories();
        };
    }, []);

    // Create tree
    useEffect(() => {
        if (!initalizedTreeData && (trialGroupLaboratories?.length > 0 || trialInstitutionLaboratories?.length > 0)) {
            setCreatingTree(true);

            const createLaboratoryNodes = (trialLaboratories: Dtos.TrialGroupLaboratory[] | Dtos.TrialInstitutionLaboratory[], showCeased: boolean, nodeIdPrefix: string) => {
                let children =
                    trialLaboratories
                        ?.filter((x) => showCeased ? true : !x.dateCeased)
                        ?.sort((a, b) => a?.laboratory?.name.localeCompare(b?.laboratory?.name))
                        ?.map((x) => ({
                            id: `${nodeIdPrefix}-laboratory-${x?.laboratory?.id}`,
                            data: (
                                <div className={`d-flex align-items-center ${showCeased ? x?.dateCeased ? "ceased" : "" : ""}`}>
                                    <div 
                                        className="d-flex align-items-center justify-content-center" 
                                        style={{ width: "25px" }}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.FLASKVIAL} />
                                    </div>
                                    <div 
                                        className="d-flex align-items-center"
                                        style={{ width: '500px' }}
                                    >
                                        <span className="pl-4 mb-0">
                                            {x?.laboratory?.name}
                                        </span>
                                    </div>
                                    <div 
                                        className="d-flex align-items-center"
                                    >
                                        <span className="pl-4 mb-0">
                                            {
                                                x?.dateCeased
                                                    ? moment(x?.dateCeased, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                                    : ""
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="d-flex align-items-center">
                                <div className="d-flex align-items-center justify-content-center" style={{ width: "25px" }}></div>
                                <div className="d-flex align-items-center" style={{ width: "500px" }}>
                                    <strong className="pl-4 mb-0">Name</strong>
                                </div>
                                <div className="d-flex align-items-center">
                                    <strong className="pl-4 mb-0">Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createInstitutionNodes = (institutions: Dtos.Institution[], trialInstitutionLaboratories: Dtos.TrialInstitutionLaboratory[], showCeased: boolean) => {
                let institutionNodes =
                    institutions
                        ?.sort((a, b) => a?.name?.localeCompare(b?.name))
                        ?.map((x) => ({
                            id: `institution-${x.id}`,
                            data: (
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "25px" }}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.INSTITUTION} />
                                    </div>
                                    <span
                                        className="pl-4 mb-0"
                                        style={{ width: "500px" }}
                                    >
                                        {x?.name}
                                    </span>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "50px" }}
                                    >
                                        <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                            {
                                                trialInstitutionLaboratories?.filter((y) =>
                                                    showCeased
                                                        ? y.trialInstitution?.institutionId == x.id
                                                        : y.trialInstitution?.institutionId == x.id && !y.dateCeased
                                                )?.length
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: true,
                            subTree: {
                                children: [
                                    ...createLaboratoryNodes(
                                        trialInstitutionLaboratories?.filter((z) => z.trialInstitution?.institutionId == x.id), 
                                        showCeased, 
                                        `institution-${x.id}`,
                                    )
                                ]
                            }
                        })) ?? [];
                return institutionNodes;
            };

            const createGroupNodes = (groups: Dtos.Group[], trialGroupLaboratories: Dtos.TrialGroupLaboratory[], showCeased: boolean) => {
                let groupNodes =
                    groups
                        ?.sort((a, b) => a?.name?.localeCompare(b?.name))
                        ?.map((x) => ({
                            id: `group-${x.id}`,
                            data: (
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "25px" }}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.GROUP} />
                                    </div>
                                    <span
                                        className="pl-4 mb-0"
                                        style={{ width: "500px" }}
                                    >
                                        {x?.name}
                                    </span>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "50px" }}
                                    >
                                        <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                            {
                                                trialGroupLaboratories?.filter((y) =>
                                                    showCeased
                                                        ? y.trialGroup?.groupId == x.id
                                                        : y.trialGroup?.groupId == x.id && !y.dateCeased
                                                )?.length
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: true,
                            subTree: {
                                children: [
                                    ...createLaboratoryNodes(
                                        trialGroupLaboratories?.filter((z) => z.trialGroup?.groupId == x.id),
                                        showCeased, 
                                        `group-${x.id}`
                                    )
                                ]
                            }
                        })) ?? [];
                return groupNodes;
            };

            let uniqueGroups: Dtos.Group[] = Array.from(
                new Map(
                    trialGroupLaboratories
                        ?.filter((x) => x?.trialGroup?.groupId && (showCeased ? true : !x.dateCeased))
                        ?.map((x) => [x.trialGroup.groupId, x.trialGroup.group])
                ).values()
            );

            let uniqueInstitutions: Dtos.Institution[] = Array.from(
                new Map(
                    trialInstitutionLaboratories
                        ?.filter((x) => x?.trialInstitution?.institutionId && (showCeased ? true : !x.dateCeased))
                        ?.map((x) => [x.trialInstitution.institutionId, x.trialInstitution.institution])
                ).values()
            );

            let treeNodes = [
                ...createInstitutionNodes(uniqueInstitutions, trialInstitutionLaboratories, showCeased),
                ...createGroupNodes(uniqueGroups, trialGroupLaboratories, showCeased)
            ];

            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        initalizedTreeData,
        trialGroupLaboratories,
        trialInstitutionLaboratories,
        showCeased,
        trialIdNumber
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id.includes("laboratory")) {
                    if (id.includes("institution") && AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialInstitutionLabView)) {
                        const institutionId = Number(id.split("-")[1]);
                        const laboratoryId = Number(id.split("-")[3]);
                        const trialInstitutionLaboratory = trialInstitutionLaboratories.find((x) => x.trialInstitution?.institutionId == institutionId && x.laboratoryId == laboratoryId);
                        navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionLaboratory?.trialInstitution?.id}/labs/${trialInstitutionLaboratory?.id}/details`);
                    }
                    if  (id.includes("group") && AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialGroupLabView)) {
                        const groupId = Number(id.split("-")[1]);
                        const laboratoryId = Number(id.split("-")[3]);
                        const trialGroupLaboratory = trialGroupLaboratories.find((x) => x.trialGroup?.groupId == groupId && x.laboratoryId == laboratoryId);
                        navigate(`/trials/${trialIdNumber}/groups/${groupId}/trialgroup/${trialGroupLaboratory?.trialGroup?.id}/labs/${trialGroupLaboratory?.id}/details`)
                    }
                }
                else {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, trialIdNumber, permissions, trialGroupLaboratories, trialInstitutionLaboratories]
    );

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    // Refresh tree
    useEffect(() => {
        if (trialGroupLaboratories?.length > 0) {
            setInitalizedTreeData(false);
        }
    }, [trialGroupLaboratories]);
    useEffect(() => {
        if (trialInstitutionLaboratories?.length > 0) {
            setInitalizedTreeData(false);
        }
    }, [trialInstitutionLaboratories]);

    const renderContent = useMemo(() => {
        return (
            <>
                <div className="row">
                    <div className="col-md-12 col-lg-auto mb-2">
                        <h1>Trial Laboratories</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={onChange}
                            labelA="Hide ceased"
                            labelB="Show ceased"
                            style={{ minWidth: "150px" }}
                        />
                    </div>
                </div>
                {!showTrialLaboratories ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialLaboratories ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no trial laboratories
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </>
        );
    }, [
        showCeased,
        onChange,
        showTrialLaboratories,
        treeData,
        handleToggle,
        toggleState
    ]);

    return (
        <RestrictedLayout
            loading={
                loadingTrial || 
                loadingTrialGroupLaboratories || 
                loadingTrialInstitutionLaboratories
            }
            permission={Dtos.Permission.TrialLaboratoryView}
        >
            { renderContent }
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialLaboratoriesPageProps> => {
    let trial: Dtos.Trial | undefined = !(state.trials.formData instanceof Array)
        ? state.trials.formData
        : undefined;
    let trialGroupLaboratories: Dtos.TrialGroupLaboratory[] | undefined = (state.trialGroupLaboratory.formData instanceof Array)
        ? state.trialGroupLaboratory.formData
        : undefined;
    let trialInstitutionLaboratories: Dtos.TrialInstitutionLaboratory[] | undefined = (state.trialInstitutionLaboratory.formData instanceof Array)
            ? state.trialInstitutionLaboratory.formData
            : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {
        trial: trial!,
        loadingTrial: state.trials.loadState && state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess: state.trials.loadState && state.trials.loadState.status == RequestState.Success,
        loadTrialFailure: state.trials.loadState && state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        trialGroupLaboratories: trialGroupLaboratories!,
        loadingTrialGroupLaboratories: state.trialGroupLaboratory.loadState && state.trialGroupLaboratory.loadState.status == RequestState.Pending,
        loadTrialGroupLaboratoriesSuccess: state.trialGroupLaboratory.loadState && state.trialGroupLaboratory.loadState.status == RequestState.Success,
        loadTrialGroupLaboratoriesFailure: state.trialGroupLaboratory.loadState && state.trialGroupLaboratory.loadState.status == RequestState.Failure,
        loadTrialGroupLaboratoriesErrorCode: state.trialGroupLaboratory.loadState.errorCode,
        trialInstitutionLaboratories: trialInstitutionLaboratories!,
        loadingTrialInstitutionLaboratories: state.trialInstitutionLaboratory.loadState && state.trialInstitutionLaboratory.loadState.status == RequestState.Pending,
        loadTrialInstitutionLaboratoriesSuccess: state.trialInstitutionLaboratory.loadState && state.trialInstitutionLaboratory.loadState.status == RequestState.Success,
        loadTrialInstitutionLaboratoriesFailure: state.trialInstitutionLaboratory.loadState && state.trialInstitutionLaboratory.loadState.status == RequestState.Failure,
        loadTrialInstitutionLaboratoriesErrorCode: state.trialInstitutionLaboratory.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialLaboratoriesPageProps> => {
    return {
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        loadTrialGroupLaboratories: bindActionCreators(TrialGroupLaboratoryActions.LoadTrialGroupLaboratoriesByTrialId, dispatch),
        clearTrialGroupLaboratories: bindActionCreators(TrialGroupLaboratoryActions.Clear, dispatch),
        loadTrialInstitutionLaboratories: bindActionCreators(TrialInstitutionLaboratoryActions.LoadTrialInstitutionLaboratoriesByTrialId, dispatch),
        clearTrialInstitutionLaboratories: bindActionCreators(TrialInstitutionLaboratoryActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialLaboratoriesPage);